import { debounce, throttle } from "lodash";
import { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useResponsive from "./useResponsive";

// This hook virtualises a list of items allowing control on how many items are rendered at a given time.
const useVirtualList = (
  //hook options; IMPORTANT: a very small value for "size" is not recommended
  { size = 50, enableAutoScroll = false },
) => {
  const [displayCount, setDisplayCount] = useState(size);
  const { scrolledToBottom } = useSelector((state) => state.layout);
  const { isMobile } = useResponsive();

  const showMore = useCallback(() => {
    setDisplayCount(displayCount + size);
  }, [displayCount, size]);

  const reset = useCallback(() => {
    if (enableAutoScroll && document) {
      document
        .getElementById("scrollContainer")
        ?.scrollTo?.({ top: 0, behavior: "smooth" });
    }
    setDisplayCount(size);
  }, [size, enableAutoScroll]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttleShowMore = useCallback(
    throttle(
      (size) => {
        if (size) {
          setDisplayCount((count) => count + size);
        }
      },
      200,
      { trailing: false },
    ),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mobileShowMore = useCallback(
    debounce((e) => {
      e.preventDefault();
      if (document && window) {
        const scrollableHeight = Math.round(
          document.documentElement.scrollHeight - window.innerHeight,
        );
        const bottom =
          window.scrollY >= scrollableHeight - 1 &&
          window.scrollY <= scrollableHeight + 1;
        if (bottom) {
          setDisplayCount((count) => count + size);
        }
      }
    }, 600),
    [],
  );

  /** if enabled, display additional list items on scroll */
  useEffect(() => {
    if (window !== undefined) {
      window.addEventListener("touchmove", mobileShowMore);
      return () => {
        window.removeEventListener("touchmove", mobileShowMore);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** if enabled, display additional list items on scroll */
  useEffect(() => {
    if (!isMobile && enableAutoScroll && scrolledToBottom) {
      throttleShowMore(size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableAutoScroll, scrolledToBottom, size, isMobile]);

  const getVirtualItems = useCallback(
    (items) => {
      if (!Array.isArray(items)) return [];
      if (items?.length < displayCount) return items;
      return items.slice(0, displayCount);
    },
    [displayCount],
  );

  return {
    showMore,
    reset,
    getVirtualItems,
  };
};

export default useVirtualList;
